import {
  ControlOutlined,
  DeleteOutlined,
  FileTextOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Button, DatePicker, Form, Input, InputNumber, Switch, message } from "antd";
import { doc, setDoc } from "firebase/firestore";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";

pdfMake.vfs = pdfFonts;

type InvoiceItem = {
  description: string;
  quantity: number;
  price: number;
};

type InvoiceFormValues = {
  issueDate: any;
  dueDate: any;
  client: string;
  reference?: string;
  items: InvoiceItem[];
};

const currency = (value: number) => `$${value.toFixed(2)}`;

export const Invoices = () => {
  const [invoiceForm] = Form.useForm();
  const nav = useNavigate();

  const createInvoice = async () => {
    try {
      const values = (await invoiceForm.validateFields()) as InvoiceFormValues;
      const items = values.items || [];

      if (!items.length) {
        message.error("Add at least one invoice item.");
        return;
      }

      const dateNow = new Date();
      const invoiceNumber = `${dateNow.getFullYear()}${dateNow.getMonth() + 1}${dateNow.getDate()}${Math.round(
        Math.random() * 10,
      )}${Math.round(Math.random() * 10)}`;

      const isGST = invoiceForm.getFieldValue("isGST") ?? true;
      const issueDate = values.issueDate?.toDate?.() || new Date();
      const dueDate = values.dueDate?.toDate?.() || new Date();

      const lineItems = items.map((item) => {
        const quantity = Number(item.quantity || 0);
        const price = Number(item.price || 0);
        const lineTotal = quantity * price;

        return {
          description: item.description,
          quantity,
          price,
          lineTotal,
        };
      });

      const subtotal = lineItems.reduce((total, item) => total + item.lineTotal, 0);
      const gstAmount = isGST ? subtotal * 0.15 : 0;
      const total = subtotal + gstAmount;

      const invoiceDoc = {
        invoiceNumber,
        client: values.client,
        clientReference: values.reference || "",
        items: lineItems,
        subtotal,
        gstAmount,
        total,
        isGST,
        dueDate: dueDate.getTime(),
        issueDate: issueDate.getTime(),
        paid: false,
      };

      await setDoc(doc(db, `invoices/${invoiceNumber}`), invoiceDoc);

      const invoiceLabel = invoiceNumber.split("20")[1] || invoiceNumber;
      const docDefinition: any = {
        pageSize: "A4",
        pageMargins: [42, 42, 42, 46],
        defaultStyle: {
          fontSize: 9.5,
          color: "#111827",
          lineHeight: 1.15,
        },
        content: [
          {
            columns: [
              {
                width: "*",
                stack: [
                  { text: "SCRYPTWORX", style: "brand" },
                  { text: "STUDIOS LIMITED", style: "brandSub" },
                  { text: "Creative digital solutions", style: "mutedGold", margin: [0, 6, 0, 0] },
                  { text: isGST ? "GST 136-354-817" : "No GST charged", style: "muted" },
                ],
              },
              {
                width: 190,
                stack: [
                  { text: "INVOICE", style: "invoiceTitle", alignment: "right" },
                  { text: invoiceLabel, style: "invoiceNumber", alignment: "right" },
                ],
              },
            ],
          },
          {
            canvas: [
              { type: "line", x1: 0, y1: 18, x2: 511, y2: 18, lineWidth: 0.7, lineColor: "#9A6D26" },
              { type: "line", x1: 0, y1: 21, x2: 80, y2: 21, lineWidth: 2, lineColor: "#F6B34B" },
            ],
          },
          {
            columns: [
              {
                width: "*",
                stack: [
                  { text: "BILL TO", style: "eyebrow" },
                  { text: values.client, style: "clientName" },
                  { text: values.reference ? `Reference: ${values.reference}` : "Reference: —", style: "muted" },
                ],
              },
              {
                width: 210,
                table: {
                  widths: [82, "*"],
                  body: [
                    [{ text: "Issue date", style: "metaLabel" }, { text: issueDate.toLocaleDateString("en-NZ"), style: "metaValue" }],
                    [{ text: "Due date", style: "metaLabel" }, { text: dueDate.toLocaleDateString("en-NZ"), style: "metaValue" }],
                    [{ text: "Amount due", style: "metaLabel" }, { text: currency(total), style: "metaTotal" }],
                  ],
                },
                layout: {
                  hLineWidth: () => 0,
                  vLineWidth: () => 0,
                  paddingLeft: () => 0,
                  paddingRight: () => 0,
                  paddingTop: () => 2,
                  paddingBottom: () => 2,
                },
              },
            ],
            margin: [0, 22, 0, 20],
          },
          {
            table: {
              headerRows: 1,
              widths: ["*", 42, 72, 82],
              body: [
                [
                  { text: "Description", style: "tableHeader" },
                  { text: "Qty", style: "tableHeader", alignment: "right" },
                  { text: "Rate", style: "tableHeader", alignment: "right" },
                  { text: "Amount", style: "tableHeader", alignment: "right" },
                ],
                ...lineItems.map((item) => [
                  { text: item.description, style: "lineDescription" },
                  { text: item.quantity.toString(), alignment: "right" },
                  { text: currency(item.price), alignment: "right" },
                  { text: currency(item.lineTotal), alignment: "right", bold: true },
                ]),
              ],
            },
            layout: {
              hLineColor: (i: number) => (i === 1 ? "#F6B34B" : "#E5E7EB"),
              hLineWidth: (i: number) => (i === 0 ? 0 : i === 1 ? 0.8 : 0.45),
              vLineWidth: () => 0,
              paddingLeft: () => 0,
              paddingRight: () => 0,
              paddingTop: (i: number) => (i === 0 ? 5 : 8),
              paddingBottom: (i: number) => (i === 0 ? 5 : 8),
            },
            margin: [0, 0, 0, 18],
          },
          {
            columns: [
              {
                width: "*",
                stack: [
                  { text: "PAYMENT DETAILS", style: "eyebrow" },
                  { text: "Account name: Scryptworx Studios Limited", style: "small" },
                  { text: "Account number: 12-3616-0018990-00", style: "small" },
                  { text: `Reference: ${invoiceLabel}`, style: "small" },
                ],
              },
              {
                width: 210,
                table: {
                  widths: ["*", 82],
                  body: [
                    [{ text: "Subtotal", style: "summaryLabel" }, { text: currency(subtotal), style: "summaryValue" }],
                    [{ text: "GST", style: "summaryLabel" }, { text: isGST ? currency(gstAmount) : "—", style: "summaryValue" }],
                    [{ text: "Total", style: "summaryTotalLabel" }, { text: currency(total), style: "summaryTotalValue" }],
                  ],
                },
                layout: {
                  hLineColor: (i: number) => (i === 2 ? "#F6B34B" : "#E5E7EB"),
                  hLineWidth: (i: number) => (i === 0 ? 0 : i === 2 ? 0.8 : 0.45),
                  vLineWidth: () => 0,
                  paddingLeft: () => 0,
                  paddingRight: () => 0,
                  paddingTop: () => 6,
                  paddingBottom: () => 6,
                },
              },
            ],
          },
          {
            text: "Thank you for your business.",
            style: "thanks",
            margin: [0, 34, 0, 0],
          },
        ],
        footer: (currentPage: number, pageCount: number) => ({
          columns: [
            { text: "scryptworxstudios.com", style: "footer" },
            { text: `Invoice ${invoiceLabel} · Page ${currentPage} of ${pageCount}`, style: "footer", alignment: "right" },
          ],
          margin: [42, 0, 42, 20],
        }),
        info: {
          title: `Scryptworx Studios Invoice ${invoiceLabel}`,
          author: "scryptworxstudios.com",
          subject: "Invoice",
          keywords: "Invoice",
        },
        styles: {
          brand: { fontSize: 18, bold: true, characterSpacing: 1.2, color: "#1D1928" },
          brandSub: { fontSize: 8.5, bold: true, characterSpacing: 2, color: "#5C6B73" },
          invoiceTitle: { fontSize: 24, bold: true, characterSpacing: 1.5, color: "#1D1928" },
          invoiceNumber: { fontSize: 10, color: "#5C6B73", margin: [0, 4, 0, 0] },
          eyebrow: { fontSize: 7.5, bold: true, characterSpacing: 1.5, color: "#F6B34B", margin: [0, 0, 0, 5] },
          clientName: { fontSize: 13, bold: true, color: "#1D1928", margin: [0, 0, 0, 3] },
          muted: { fontSize: 8.5, color: "#5C6B73" },
          mutedGold: { fontSize: 8.5, color: "#9A6D26" },
          small: { fontSize: 8.5, color: "#374151", margin: [0, 1, 0, 1] },
          metaLabel: { fontSize: 8, color: "#6B7280" },
          metaValue: { fontSize: 9, bold: true, alignment: "right" },
          metaTotal: { fontSize: 12, bold: true, alignment: "right", color: "#1D1928" },
          tableHeader: { fontSize: 7.5, bold: true, characterSpacing: 1.1, color: "#5C6B73" },
          lineDescription: { fontSize: 9.5, color: "#1D1928" },
          summaryLabel: { fontSize: 9, color: "#6B7280" },
          summaryValue: { fontSize: 9, alignment: "right" },
          summaryTotalLabel: { fontSize: 12, bold: true, color: "#1D1928" },
          summaryTotalValue: { fontSize: 12, bold: true, alignment: "right", color: "#1D1928" },
          thanks: { fontSize: 9, italics: true, color: "#6B7280" },
          footer: { fontSize: 7.5, color: "#9CA3AF" },
        },
      };

      pdfMake.createPdf(docDefinition).open();
      message.success("Invoice created.");
    } catch (err: any) {
      console.error(err);
      message.error("Failed to create invoice.");
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#1f1e23] text-white selection:bg-[#F6B34B] selection:text-[#1D1928]">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_top,_rgba(246,179,75,0.08),_transparent_35%),linear-gradient(to_bottom,_rgba(0,0,0,0.15),_transparent)]" />

      <main className="relative z-10 mx-auto flex w-full max-w-7xl flex-col gap-8 px-6 py-10 md:py-16">
        <header className="border border-white/10 bg-[#1D1928]/50 p-6 backdrop-blur-sm md:p-10">
          <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#5C6B73] animate-pulse">
                {"// INVOICE.BUILDER :: MANUAL_ITEMS"}
              </p>
              <h1 className="mt-5 font-oswald text-5xl uppercase leading-none tracking-tighter text-[#F6B34B] md:text-7xl">
                Invoicing
              </h1>
              <p className="mt-5 max-w-xl border-l-2 border-[#F6B34B] pl-4 font-mono text-sm leading-relaxed text-gray-300 md:text-base">
                Create custom invoices with manually entered line items, quantities,
                and prices.
              </p>
            </div>

            <button
              className="border border-[#5C6B73] px-5 py-3 font-mono text-xs uppercase tracking-widest text-[#5C6B73] transition-all hover:border-[#F6B34B] hover:text-[#F6B34B]"
              onClick={() => nav("/tools")}
              type="button"
            >
              <ControlOutlined /> Tools
            </button>
          </div>
        </header>

        <section className="border border-[#9A6D26]/20 bg-black/10">
          <div className="border-b border-white/10 px-5 py-4 md:px-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C6B73]">
              {"// invoice.form"}
            </p>
            <h2 className="mt-2 font-oswald text-2xl uppercase tracking-tight text-white">
              Invoice Details
            </h2>
            <div className="mt-3 h-1 w-12 bg-[#F6B34B]" />
          </div>

          <div className="p-5 md:p-8">
            <Form
              form={invoiceForm}
              layout="vertical"
              initialValues={{
                isGST: true,
                items: [{ description: "", quantity: 1, price: 0 }],
              }}
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Form.Item
                  name="issueDate"
                  label={<span className="font-mono uppercase text-[#5C6B73]">Issue Date</span>}
                  rules={[{ required: true, message: "Issue date is required" }]}
                >
                  <DatePicker className="w-full" />
                </Form.Item>
                <Form.Item
                  name="dueDate"
                  label={<span className="font-mono uppercase text-[#5C6B73]">Due Date</span>}
                  rules={[{ required: true, message: "Due date is required" }]}
                >
                  <DatePicker className="w-full" />
                </Form.Item>
                <Form.Item
                  name="client"
                  label={<span className="font-mono uppercase text-[#5C6B73]">Client</span>}
                  rules={[{ required: true, message: "Client is required" }]}
                >
                  <Input placeholder="Client name" />
                </Form.Item>
                <Form.Item
                  name="reference"
                  label={<span className="font-mono uppercase text-[#5C6B73]">Reference</span>}
                >
                  <Input placeholder="Customer reference / job" />
                </Form.Item>
              </div>

              <Form.Item
                name="isGST"
                valuePropName="checked"
                label={<span className="font-mono uppercase text-[#5C6B73]">GST</span>}
              >
                <Switch checkedChildren="GST" unCheckedChildren="No GST" />
              </Form.Item>

              <div className="mt-8 border border-white/10 bg-white/[0.02]">
                <div className="border-b border-white/10 px-5 py-4">
                  <h3 className="font-oswald text-xl uppercase tracking-tight text-[#F6B34B]">
                    Line Items
                  </h3>
                </div>

                <Form.List name="items">
                  {(fields, { add, remove }) => (
                    <div className="space-y-4 p-5">
                      {fields.map((field, index) => (
                        <div
                          key={field.key}
                          className="grid grid-cols-1 gap-3 border border-white/10 bg-black/20 p-4 md:grid-cols-[1fr_120px_160px_48px]"
                        >
                          <Form.Item
                            {...field}
                            name={[field.name, "description"]}
                            label={index === 0 ? <span className="font-mono uppercase text-[#5C6B73]">Item</span> : undefined}
                            rules={[{ required: true, message: "Item required" }]}
                          >
                            <Input placeholder="Item description" />
                          </Form.Item>

                          <Form.Item
                            {...field}
                            name={[field.name, "quantity"]}
                            label={index === 0 ? <span className="font-mono uppercase text-[#5C6B73]">Qty</span> : undefined}
                            rules={[{ required: true, message: "Qty required" }]}
                          >
                            <InputNumber min={0} className="w-full" />
                          </Form.Item>

                          <Form.Item
                            {...field}
                            name={[field.name, "price"]}
                            label={index === 0 ? <span className="font-mono uppercase text-[#5C6B73]">Price</span> : undefined}
                            rules={[{ required: true, message: "Price required" }]}
                          >
                            <InputNumber min={0} precision={2} className="w-full" prefix="$" />
                          </Form.Item>

                          <div className="flex items-end">
                            <Button
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => remove(field.name)}
                              disabled={fields.length === 1}
                            />
                          </div>
                        </div>
                      ))}

                      <Button
                        icon={<PlusOutlined />}
                        onClick={() => add({ description: "", quantity: 1, price: 0 })}
                      >
                        Add Item
                      </Button>
                    </div>
                  )}
                </Form.List>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button type="primary" icon={<FileTextOutlined />} onClick={createInvoice}>
                  Create Invoice
                </Button>
                <Button onClick={() => invoiceForm.resetFields()}>Reset</Button>
              </div>
            </Form>
          </div>
        </section>
      </main>
    </div>
  );
};
